"use client";
import React from "react";
import Image from "next/image";
import { motion } from "framer-motion";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";
import { GROQImageLoader } from "@/sanity/lib/definitions";
import { resolvePageLink } from "@/sanity/globals/CTA";
import type { PageAffiliateSchema } from "@/sanity/pages/PageAffiliateTemplate";
import Hero from "../pageComponents/affiliate/Hero";
import SummerSaleTopBanner from "@/components/global/SummerSaleTopBanner";
import SummerSalePopup from "@/components/global/SummerSalePopup";
import AffiliateBanners from "@/components/global/AffiliateBanners";
import LaunchEventWidget from "@/components/global/LaunchEventWidget";
import ExclusiveSection from "../pageComponents/affiliate/Exclusive";
import Deals from "../pageComponents/affiliate/Deals";
import Rules from "../pageComponents/affiliate/Rules";
import TradersSaySection from "../pageComponents/affiliate/TraderSay";
import TestimonialsSection from "../pageComponents/affiliate/Testimonials";
import IntroSection from "../pageComponents/affiliate/Intro";

import logo from "@/public/affiliate-template/logo.svg";
import lastBG from "@/public/affiliate-overview/last-bg.png";
import lastStatue from "@/public/affiliate-template/last-statue.png";
import footerDots from "@/public/affiliate-overview/footer-dots.png";
import logoBlack from "@/public/global/logo-black.svg";
import { ChevronRight } from "lucide-react";

const ease = [0.25, 0.1, 0.25, 1] as const;

export default function PageAffiliateJSX({ data }: { data: PageAffiliateSchema }) {
  const summerSaleCtaUrl =
    data?.heroCTA?.cta?.link?.url ?? data?.summerSale?.ctaUrl ?? "#";
  return (
    <>
      <main className="">
        {data?.summerSale?.isActive && !data?.settings?.hideSummerSale && (
          <SummerSaleTopBanner
            data={data.summerSale}
            ctaUrl={summerSaleCtaUrl}
            language={data.settings?.language}
          />
        )}
        {data?.summerSale?.isActive && !data?.settings?.hideSummerSale && (
          <SummerSalePopup
            data={data.summerSale}
            ctaUrl={summerSaleCtaUrl}
            language={data.settings?.language}
          />
        )}
        {/* ctaUrl: the partner link, so the banner CTAs keep the `?aff=` /
            `?coupon=` attribution and the Black Month popup can show the code —
            the same URL the Launch Event widget below receives. */}
        <AffiliateBanners
          settings={data?.settings}
          ctaUrl={data?.heroCTA?.cta?.link?.url ?? data?.summerSale?.ctaUrl}
          enableFlashSale
        />
        <LaunchEventWidget
          data={data?.launchEvent}
          language={data?.settings?.language}
          affCtaUrl={data?.heroCTA?.cta?.link?.url}
        />
        <Hero data={data} />

        <ExclusiveSection data={data} />

        <Deals data={data} />

        <Rules data={data} />

        <TradersSaySection data={data} />

        <TestimonialsSection data={data} />

        <IntroSection data={data} />

        {/* ── Last ──────────────────────────────────────────── */}
        {(data?.lastPartner?.image || data?.lastPartner?.name || data?.lastTitle || data?.lastCta) && (
          <section className="relative w-full p-[1rem] bg-[#B4B4B4]/[15%] lg:p-[5rem] lg:h-[120rem] overflow-clip">
            <div className="relative w-full text-center pt-[6.5rem] h-[82.5rem] overflow-clip pb-[96rem] lg:h-[119.375rem] lg:pt-[8rem]">
              <div className="px-[2]">
                <div className="flex justify-center items-center w-full gap-[1.5rem] mb-[2rem]">
                  <Image
                    src={logo}
                    alt=""
                    className="hidden lg:block"
                  />
                  <ChevronRight className="hidden lg:block" />
                  <div className="flex items-center gap-[1.5rem]">
                    {data?.lastPartner?.image && (
                      <Image
                        src={data?.lastPartner?.image}
                        alt=""
                        className="w-[6.5rem] shrink-0"
                      />
                    )}
                    <p className="m-0 font-geist font-[600] text-[2.5rem] text-[#103037] leading-[116%]">
                      {data?.lastPartner && data?.lastPartner.name}
                    </p>
                  </div>
                </div>
                {data?.lastTitle && (
                  <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.35 }}
                    transition={{ duration: 0.8, ease, delay: 0.15 }}
                    className="richtext mx-auto lg:[&_span]:font-display lg:[&_span]:font-[700] [&_span]:font-instrument-sans [&_span]:font-[600] [&_span]:text-[#103037] lg:[&_span]:text-[#65A38A] z-[5] relative w-full prose-h1:tracking-[-.25rem] prose-h6:font-blacker prose-h6:italic prose-h6:text-[#103037] prose-h6:text-[2.25rem] prose-h6:mb-[2rem] prose-p:w-[min(63.625rem,100%)] prose-p:text-[2rem] prose-strong:!font-[600] prose-em:font-[400] prose-em:text-[#003828] prose-em:font-blacker prose-headings:font-geist prose-headings:font-[500] prose-headings:mb-[2rem] prose-headings:text-[5rem] lg:prose-headings:text-[8rem]">
                    <PortableText
                      value={data.lastTitle}
                      components={richTextComponent}
                    />
                  </motion.div>
                )}
                {data?.lastCta && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.35 }}
                    transition={{ duration: 0.8, ease, delay: 0.3 }}
                    className="flex relative justify-center mt-[3rem] z-[5] lg:[&_.button]:!min-h-[5.75rem]">
                    <CTAButton data={data.lastCta} />
                  </motion.div>
                )}
              </div>
            </div>
            <Image
              src={lastBG}
              alt=""
              quality={100}
              className="absolute inset-0 pointer-events-none z-[1] mix-blend-exclusion"
            />

            <Image
              src={lastStatue}
              alt=""
              quality={100}
              className="absolute bottom-0 left-1/2 -translate-x-1/2 pointer-events-none z-[1] w-[64rem]"
            />
            <div className="absolute bottom-0 w-full bg-gradient-to-t h-[22.25rem] from-[#F4F4F4] to-[#F4F4F4]/0 z-[2]" />
          </section>
        )}
        {/* ── Footer Disclaimer ─────────────────────────────── */}
        {(data?.footerDisclaimerCopyright ||
          (data?.footerDisclaimerLegalLinks && data.footerDisclaimerLegalLinks.length > 0) ||
          data?.footerDisclaimerText) && (
          <section className="relative w-full bg-[#F4F4F4] py-[6rem] px-[3rem] lg:py-[8rem] lg:px-[4rem]">
            <Image
              src={footerDots}
              alt=""
              className="object-cover absolute inset-0 w-full h-full pointer-events-none mix-blend-exclusion"
            />
            <div className="container flex flex-col mx-auto gap-[4rem] w-[min(140rem)]">
              {(data?.footerDisclaimerCopyright ||
                (data?.footerDisclaimerLegalLinks && data.footerDisclaimerLegalLinks.length > 0)) && (
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.35 }}
                  transition={{ duration: 0.6, ease }}
                  className="flex relative flex-col justify-between items-center lg:flex-row gap-[2rem] lg:h-[3.5rem]">
                  {data?.footerDisclaimerCopyright && (
                    <p className="m-0 text-[#103037]/70 text-[1.75rem] lg:text-[2rem]">
                      {data.footerDisclaimerCopyright}
                    </p>
                  )}
                  <div className="lg:absolute lg:top-1/2 lg:left-1/2 lg:-translate-x-1/2 lg:-translate-y-1/2">
                    <Image
                      src={logoBlack}
                      alt=""
                      className="pointer-events-none"
                    />
                  </div>
                  {data?.footerDisclaimerLegalLinks && data.footerDisclaimerLegalLinks.length > 0 && (
                    <ul className="flex flex-wrap items-center p-0 m-0 list-none gap-x-[1rem] gap-y-[1rem]">
                      {data.footerDisclaimerLegalLinks.map((item, idx) => {
                        const href = resolvePageLink(item.link, data?.settings?.language) ?? "#";
                        const openInNewTab = item.link?.openInNewTab;
                        const isLast = idx === (data.footerDisclaimerLegalLinks?.length ?? 0) - 1;
                        return (
                          <React.Fragment key={idx}>
                            <li className="m-0">
                              <a
                                href={href}
                                target={openInNewTab ? "_blank" : undefined}
                                rel={openInNewTab ? "noopener noreferrer" : undefined}
                                className="transition-all hover:underline text-black/80 text-[1.75rem] underline-offset-4 font-geist lg:text-[2rem]">
                                {item.title}
                              </a>
                            </li>
                            {!isLast && <p className="m-0 text-black/80 text-[1.75rem] lg:text-[2rem]">/</p>}
                          </React.Fragment>
                        );
                      })}
                    </ul>
                  )}
                </motion.div>
              )}
              <div className="w-full border-t h-[.125rem] border-t-black/10" />
              {data?.footerDisclaimerText && (
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.6, ease, delay: 0.1 }}
                  className="richtext richtext-center prose-p:text-black/60 prose-p:text-[1.75rem] prose-p:leading-[150%] prose-p:font-geist">
                  <PortableText
                    value={data.footerDisclaimerText}
                    components={richTextComponent}
                  />
                </motion.div>
              )}
            </div>
          </section>
        )}
      </main>
    </>
  );
}
